import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BellRing, X, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errorMessage } from "@/lib/api";

export default function Alerts() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["alerts"], queryFn: () => api.get("/admin/settings/alerts").then((r) => r.data) });
  const [emails, setEmails] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data) setEmails(data.emails); }, [data]);

  const add = (e) => {
    e.preventDefault();
    const list = draft.split(/[\s,;]+/).map((x) => x.trim().toLowerCase()).filter(Boolean);
    setEmails([...new Set([...emails, ...list])]); setDraft("");
  };

  const save = async () => {
    setBusy(true);
    try { const r = await api.put("/admin/settings/alerts", { emails }); setEmails(r.data.emails); qc.setQueryData(["alerts"], r.data); toast.success("Alert recipients saved"); }
    catch (e) { toast.error(errorMessage(e)); }
    setBusy(false);
  };

  const test = async () => {
    try { const r = await api.post("/admin/settings/alerts/test"); toast.success(`Test email sent to ${r.data.sent_to.length} recipient(s)`); }
    catch (e) { toast.error(errorMessage(e)); }
  };

  const dirty = JSON.stringify([...emails].sort()) !== JSON.stringify(data?.emails || []);

  return (
    <div className="space-y-8 max-w-2xl">
      <div className="rise">
        <p className="font-mono text-xs uppercase tracking-wider text-indigo-400">Notifications</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Sync Alerts</h1>
        <p className="text-slate-400 mt-2 text-sm sm:text-base">These people get an email when a sync has failed files or errors out.</p>
      </div>
      <div className="bg-[#0F172A] border border-slate-800 rounded-xl p-5 sm:p-6 space-y-5 rise" style={{ animationDelay: "80ms" }}>
        <form onSubmit={add} className="flex gap-2">
          <Input data-testid="alert-email-input" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="name@dhwaniris.com, other@dhwaniris.com" className="bg-[#080C14] border-slate-700 h-10" />
          <Button data-testid="alert-email-add-button" type="submit" variant="outline" className="h-10 border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800 hover:text-white">Add</Button>
        </form>
        <div className="flex flex-wrap gap-2" data-testid="alert-email-list">
          {emails.map((e) => (
            <span key={e} data-testid="alert-email-chip" className="inline-flex items-center gap-1.5 font-mono text-xs bg-slate-800 border border-slate-700 rounded-full pl-3 pr-1.5 py-1">
              {e}
              <button data-testid="alert-email-remove-button" onClick={() => setEmails(emails.filter((x) => x !== e))} className="p-0.5 rounded-full hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"><X className="h-3 w-3" /></button>
            </span>
          ))}
          {!emails.length && <p className="text-sm text-slate-500 flex items-center gap-2"><BellRing className="h-4 w-4" />No recipients yet, so no alerts will be sent.</p>}
        </div>
        <div className="flex gap-2 pt-2 border-t border-slate-800">
          <Button data-testid="alert-save-button" onClick={save} disabled={busy || !dirty} className="bg-indigo-500 hover:bg-indigo-600 active:scale-95 transition-transform">Save recipients</Button>
          <Button data-testid="alert-test-button" onClick={test} disabled={dirty || !emails.length} variant="ghost" className="text-slate-300 hover:bg-slate-800 hover:text-white"><Send className="h-4 w-4 mr-2" />Send test email</Button>
        </div>
      </div>
    </div>
  );
}
